import type { PreviewCategory } from '@/types'

// The four canonical layers emitted by the AI engine. layerNumber is a
// structural property of every persisted game row and live SSE blueprint
// entry, so these maps are total over the {1,2,3,4} domain.

export const LAYER_LABELS: Record<number, string> = {
  1: 'Pattern Practice',
  2: 'Word Meaning',
  3: 'Connected Text',
  4: 'Comprehension',
  // Layer 5 — the "Sound & Spelling" strand, user-facing "Word Building"
  // (HOP-148). Only present at stages 2A/2B; displays between Word Meaning
  // and Connected Text (see displayOrder).
  5: 'Word Building',
}

// Maps layerNumber → PreviewCategory bucket used by GalleryGuidePreview.
// Index 0 is null because layers are 1-indexed. Layer 5 (Word Building) groups
// with pattern-practice (both are decoding-family) to avoid a new bucket type.
export const LAYER_TO_BUCKET: ReadonlyArray<PreviewCategory | null> = [
  null,
  'pattern-practice',
  'words-meaning',
  'connected-text',
  'story-connection',
  'pattern-practice',
] as const

// Tailwind color classes for the layer badge in GameNode / GamePreviewModal.
// Re-uses the existing reading-guides palette but keys off layerNumber.
export const LAYER_COLORS: Record<number, string> = {
  1: 'bg-orange-100 text-orange-700',
  2: 'bg-amber-100 text-amber-700',
  3: 'bg-blue-100 text-blue-700',
  4: 'bg-indigo-100 text-indigo-700',
  5: 'bg-teal-100 text-teal-700',
}

// Presentation order for a game layer. Wire identity (layerNumber) is unchanged
// everywhere — this ONLY controls where a layer sorts in the UI. Word Building
// (5) sits between Word Meaning (2) and Connected Text (3) per HOP-148, so it
// gets a fractional key; every other layer sorts by its own number.
export function displayOrder(layerNumber: number | null | undefined): number {
  if (layerNumber == null) return Number.MAX_SAFE_INTEGER
  return layerNumber === 5 ? 2.5 : layerNumber
}

export function layerBadgeText(layerNumber: number | null | undefined): string | null {
  if (layerNumber == null) return null
  const label = LAYER_LABELS[layerNumber]
  return label ? `Layer ${layerNumber} · ${label}` : null
}

// Forces a corrected display name for mechanics whose backend template/layer
// name doesn't match what the game should be called in the UI — e.g. the
// content team wants "Meaning Match", but the template/layer_name coming
// back from /api/content/templates (or a saved guide row) still says "Match
// and Cover". This wins over whatever the backend sends, regardless of which
// of the mechanic's known aliases is in play. Drop an entry once the backend
// template is renamed and confirmed consistent — see ticket "incorrect cover
// image for a meaning match" / the "Match and Cover" title follow-up.
export const GAME_NAME_OVERRIDES: Record<string, string> = {
  match_and_cover: 'Meaning Match',
  grid_match_board: 'Meaning Match',
  grid_match_cards: 'Meaning Match',
}

export function gameDisplayName(mechanicId: string, fallbackName: string): string {
  return GAME_NAME_OVERRIDES[mechanicId] ?? fallbackName
}
